<template>
  <!-- Render condicional por modo: docked OR floating, nunca ambos -->
  <div v-if="overlayOpen && activeCall" class="sos-panel-wrapper">
    <!-- Modo DOCKED: bloque natural dentro del slot del leftstack -->
    <section
      v-if="!isFloating"
      class="sos-panel sos-panel--docked"
    >
      <header class="sos-panel__header">
        <div class="sos-panel__avatar">{{ avatarInitial }}</div>
        <div class="sos-panel__header-body">
          <div class="sos-panel__name" :title="reportantName">{{ reportantName }}</div>
          <div class="sos-panel__timer">
            <v-icon x-small color="white" class="mr-1">mdi-clock-outline</v-icon>
            {{ elapsedTime }}
          </div>
        </div>

        <v-btn
          v-if="!isChatOnly && !isClientEnded"
          icon dark x-small
          v-auto-tooltip="micTitle"
          class="sos-panel__header-btn"
          @click.stop="toggleMic"
        >
          <v-icon small>{{ isMicOn ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
        </v-btn>

        <v-btn
          icon dark x-small
          v-auto-tooltip="'Modo flotante'"
          class="sos-panel__header-btn"
          @click.stop="toggleFloatingMode"
        >
          <v-icon small>mdi-picture-in-picture-bottom-right</v-icon>
        </v-btn>

        <!-- Único botón de la barra del panel: "Salir" (leave_call). El operador
             DEJA la llamada sin terminarla; sigue activa para los demás operadores
             y el reportante, y puede reanudarse. La llamada SOLO se finaliza por
             completo (end_call) desde el botón del drawer derecho. Usa el icono de
             finalizar y color rojo por pedido del operador. Se oculta si el
             reportante ya colgó (CLIENT_END_CALL), igual que el "DEJAR" legacy. -->
        <v-btn
          v-if="!isClientEnded"
          dark x-small text
          color="error"
          v-auto-tooltip="'Dejar llamada'"
          class="sos-panel__header-leave"
          @click="salir"
        >
          <v-icon left x-small>mdi-phone-hangup</v-icon>
          Salir
        </v-btn>
      </header>

      <!-- Video / Audio (solo si no es chat-only) -->
      <section v-if="!isChatOnly" class="sos-panel__media">
        <template v-if="isVideoCall">
          <video
            ref="videoEl"
            class="sos-panel__video"
            autoplay
            playsinline
            muted
          />
        </template>
        <template v-else>
          <div class="sos-panel__audio-only">
            <div class="sos-panel__audio-avatar">{{ avatarInitial }}</div>
            <v-icon color="white" size="24" class="mt-1">{{ callTypeIcon }}</v-icon>
            <div class="sos-panel__audio-label">{{ callTypeLabel }}</div>
          </div>
        </template>

        <audio ref="audioEl" autoplay />

        <div v-if="isVideoCall" class="sos-panel__video-controls">
          <v-btn
            icon
            dark
            small
            @click="swapCamera"
            title="Cambiar cámara del reportante"
          >
            <v-icon>mdi-camera-flip</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            small
            @click="toggleFullscreen"
            title="Expandir video"
          >
            <v-icon>mdi-fullscreen</v-icon>
          </v-btn>
        </div>
      </section>

      <!-- Ficha crítica colapsable -->
      <section class="sos-panel__critical">
        <div
          v-for="(field, i) in criticalFields"
          :key="'c-' + i"
          class="sos-panel__field"
        >
          <span class="sos-panel__field-label">{{ field.label }}</span>
          <span class="sos-panel__field-value">{{ field.value || '—' }}</span>
        </div>
        <div class="sos-panel__critical-actions">
          <button
            v-if="canSearchPersonMurphy"
            type="button"
            class="sos-panel__murphy-btn"
            v-auto-tooltip="'Buscar al reportante en Murphy'"
            @click="buscarPersonaMurphy"
          >
            <v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>
            Buscar en Murphy
          </button>
          <button
            v-if="hasExtraFields"
            type="button"
            class="sos-panel__expand-btn"
            @click="criticalExpanded = !criticalExpanded"
          >
            <v-icon x-small>{{ criticalExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ criticalExpanded ? 'ver menos' : 'ver más' }}
          </button>
        </div>
        <template v-if="criticalExpanded">
          <div
            v-for="(field, i) in extraFields"
            :key="'e-' + i"
            class="sos-panel__field"
          >
            <span class="sos-panel__field-label">{{ field.label }}</span>
            <span class="sos-panel__field-value">{{ field.value || '—' }}</span>
          </div>
        </template>
      </section>

      <!-- Chat lista (flex 1) + input al fondo -->
      <section class="sos-panel__chat">
        <div ref="chatList" class="sos-panel__chat-list">
          <div
            v-for="(msg, i) in filteredMessages"
            :key="msg._id || i"
            class="sos-panel__chat-row"
            :class="{ 'is-operator': isOperatorMessage(msg) }"
          >
            <div class="sos-panel__chat-bubble">
              <span v-if="msg.senderName" class="sos-panel__chat-sender">{{ msg.senderName }}</span>
              {{ msg.message }}
              <span class="sos-panel__chat-time">{{ formatMsgTime(msg.createdAt) }}</span>
            </div>
          </div>
          <div v-if="filteredMessages.length === 0" class="sos-panel__chat-empty">
            Sin mensajes
          </div>
        </div>

        <v-alert
          v-if="isClientEnded"
          type="info"
          dense
          class="sos-panel__ended-alert"
        >
          El reportante finalizó la llamada.
        </v-alert>
        <div v-else class="sos-panel__chat-input-row">
          <v-text-field
            v-model="chatInput"
            dense
            hide-details
            outlined
            placeholder="Mensaje…"
            @keydown.enter.prevent="enviar"
          />
          <v-btn icon small class="ml-1" :disabled="!chatInput.trim()" @click="enviar">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
      </section>
    </section>

    <!-- Modo FLOATING: ventana suelta con position: fixed -->
    <aside
      v-else
      ref="root"
      class="sos-panel sos-panel--floating"
      :style="rootStyle"
    >
      <div v-if="isInteracting" class="sos-interaction-overlay"></div>

      <!-- 8 resize handles (idéntico al SOSOverlay actual) -->
      <div class="sos-resize-handle sos-resize-handle--n"  @mousedown.stop="_startResize('n',  $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--e"  @mousedown.stop="_startResize('e',  $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--s"  @mousedown.stop="_startResize('s',  $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--w"  @mousedown.stop="_startResize('w',  $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--nw" @mousedown.stop="_startResize('nw', $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--ne" @mousedown.stop="_startResize('ne', $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--sw" @mousedown.stop="_startResize('sw', $event)"></div>
      <div class="sos-resize-handle sos-resize-handle--se" @mousedown.stop="_startResize('se', $event)"></div>

      <header
        class="sos-panel__header sos-panel__header--draggable"
        @mousedown="onHeaderMouseDown"
      >
        <div class="sos-panel__avatar">{{ avatarInitial }}</div>
        <div class="sos-panel__header-body">
          <div class="sos-panel__name" :title="reportantName">{{ reportantName }}</div>
          <div class="sos-panel__timer">
            <v-icon x-small color="white" class="mr-1">mdi-clock-outline</v-icon>
            {{ elapsedTime }}
          </div>
        </div>

        <v-btn
          v-if="!isChatOnly && !isClientEnded"
          icon dark x-small
          v-auto-tooltip="micTitle"
          class="sos-panel__header-btn"
          @click.stop="toggleMic"
        >
          <v-icon small>{{ isMicOn ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
        </v-btn>

        <v-btn
          icon dark x-small
          v-auto-tooltip="'Anclar al panel izquierdo'"
          class="sos-panel__header-btn"
          @click.stop="toggleFloatingMode"
        >
          <v-icon small>mdi-dock-left</v-icon>
        </v-btn>

        <!-- Único botón de la barra del panel: "Salir" (leave_call). El operador
             DEJA la llamada sin terminarla; sigue activa para los demás operadores
             y el reportante, y puede reanudarse. La llamada SOLO se finaliza por
             completo (end_call) desde el botón del drawer derecho. Usa el icono de
             finalizar y color rojo por pedido del operador. Se oculta si el
             reportante ya colgó (CLIENT_END_CALL), igual que el "DEJAR" legacy. -->
        <v-btn
          v-if="!isClientEnded"
          dark small text
          color="error"
          v-auto-tooltip="'Dejar llamada'"
          class="sos-panel__header-leave"
          @click="salir"
        >
          <v-icon left small>mdi-phone-hangup</v-icon>
          Salir
        </v-btn>
      </header>

      <div class="sos-panel__floating-body">
        <!-- Columna izq: video o audio-only (cuando no es chat-only) -->
        <div v-if="!isChatOnly" class="sos-panel__floating-media">
          <template v-if="isVideoCall">
            <video
              ref="videoElFloating"
              class="sos-panel__video sos-panel__video--floating"
              autoplay
              playsinline
              muted
            />
          </template>
          <template v-else>
            <div class="sos-panel__audio-only sos-panel__audio-only--floating">
              <div class="sos-panel__audio-avatar">{{ avatarInitial }}</div>
              <v-icon color="white" size="40" class="mt-2">{{ callTypeIcon }}</v-icon>
              <div class="sos-panel__audio-label">{{ callTypeLabel }}</div>
            </div>
          </template>
          <audio ref="audioElFloating" autoplay />

          <div v-if="isVideoCall" class="sos-panel__video-controls">
            <v-btn
              icon
              dark
              small
              @click="swapCamera"
              title="Cambiar cámara del reportante"
            >
              <v-icon>mdi-camera-flip</v-icon>
            </v-btn>
            <v-btn
              icon
              dark
              small
              @click="toggleFullscreen"
              title="Expandir video"
            >
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Columna der: ficha + chat -->
        <div class="sos-panel__floating-info">
          <section class="sos-panel__critical sos-panel__critical--floating">
            <div
              v-for="(field, i) in criticalFields"
              :key="'fc-' + i"
              class="sos-panel__field"
            >
              <span class="sos-panel__field-label">{{ field.label }}</span>
              <span class="sos-panel__field-value">{{ field.value || '—' }}</span>
            </div>
            <div class="sos-panel__critical-actions">
              <button
                v-if="canSearchPersonMurphy"
                type="button"
                class="sos-panel__murphy-btn"
                v-auto-tooltip="'Buscar al reportante en Murphy'"
                @click="buscarPersonaMurphy"
              >
                <v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>
                Buscar en Murphy
              </button>
              <button
                v-if="hasExtraFields"
                type="button"
                class="sos-panel__expand-btn"
                @click="criticalExpanded = !criticalExpanded"
              >
                <v-icon x-small>{{ criticalExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ criticalExpanded ? 'ver menos' : 'ver más' }}
              </button>
            </div>
            <template v-if="criticalExpanded">
              <div
                v-for="(field, i) in extraFields"
                :key="'fe-' + i"
                class="sos-panel__field"
              >
                <span class="sos-panel__field-label">{{ field.label }}</span>
                <span class="sos-panel__field-value">{{ field.value || '—' }}</span>
              </div>
            </template>
          </section>

          <section class="sos-panel__chat sos-panel__chat--floating">
            <div ref="chatListFloating" class="sos-panel__chat-list">
              <div
                v-for="(msg, i) in filteredMessages"
                :key="msg._id || i"
                class="sos-panel__chat-row"
                :class="{ 'is-operator': isOperatorMessage(msg) }"
              >
                <div class="sos-panel__chat-bubble">
                  <span v-if="msg.senderName" class="sos-panel__chat-sender">{{ msg.senderName }}</span>
                  {{ msg.message }}
                  <span class="sos-panel__chat-time">{{ formatMsgTime(msg.createdAt) }}</span>
                </div>
              </div>
              <div v-if="filteredMessages.length === 0" class="sos-panel__chat-empty">
                Sin mensajes
              </div>
            </div>

            <v-alert
              v-if="isClientEnded"
              type="info"
              dense
              class="sos-panel__ended-alert"
            >
              El reportante finalizó la llamada.
            </v-alert>
            <div v-else class="sos-panel__chat-input-row">
              <v-text-field
                v-model="chatInput"
                dense
                hide-details
                outlined
                placeholder="Mensaje…"
                @keydown.enter.prevent="enviar"
              />
              <v-btn icon small class="ml-1" :disabled="!chatInput.trim()" @click="enviar">
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </div>
          </section>
        </div>
      </div>
    </aside>

    <!-- Diálogo de confirmación salir (leave_call) -->
    <v-dialog v-model="confirmSalirOpen" max-width="360" dark>
      <v-card class="sos-confirm-card">
        <v-card-title class="text-subtitle-1">Salir de la llamada</v-card-title>
        <v-card-text>
          Saldrás de la llamada con <strong>{{ reportantName }}</strong>. La
          llamada seguirá activa para los demás operadores y el reportante, y
          podrás reanudarla.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="confirmSalirOpen = false">Cancelar</v-btn>
          <v-btn color="white" small text @click="confirmarSalir">
            <v-icon left small>mdi-logout</v-icon>
            Salir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- Fullscreen video overlay -->
    <transition name="sos-fullscreen">
      <div
        v-if="isVideoExpanded && isVideoCall"
        class="sos-fullscreen-overlay"
        @click.self="closeFullscreen"
      >
        <div class="sos-fullscreen-box">
          <v-btn
            icon
            dark
            class="sos-fullscreen-close"
            @click="closeFullscreen"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <video
            ref="videoFullscreenEl"
            class="sos-fullscreen-video"
            autoplay
            playsinline
            muted
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

// SOS_MIN_W reducido a 420 (vs 520 en SOSOverlay) para acomodar la
// ventana flotante chat-only (default 420×500). Spec §"Mínimos".
const SOS_MIN_W = 420
const SOS_MIN_H = 400
const SOS_MAX_VW = 0.80
const SOS_MAX_VH = 0.90
const MIN_VISIBLE_X = 120
const MIN_VISIBLE_Y = 40

const FLOATING_DEFAULT_W = 720
const FLOATING_DEFAULT_H = 540
const FLOATING_CHAT_ONLY_W = 420
const FLOATING_CHAT_ONLY_H = 500

export default {
  name: 'SOSPanel',

  inject: ['sosCallServiceGetter'],

  data() {
    return {
      nowTick: Date.now(),
      tickInterval: null,
      chatInput: '',
      confirmSalirOpen: false,
      isInteracting: false,
      criticalExpanded: false,
    }
  },

  computed: {
    ...mapGetters('sos-call-v2', [
      'isOverlayOpen',
      'getVideoStream',
      'getAudioStream',
      'getIsMicOn',
      'getAddressForCall',
      'getIsVideoExpanded',
    ]),
    ...mapState('sos-call-v2', {
      sosMode: state => state.mode,
      sosFloatingPos: state => state.floatingPos,
      sosFloatingSize: state => state.floatingSize,
    }),
    ...mapGetters('map', ['getSOSActiveCall']),
    ...mapGetters('sosMessages', ['getSosMessages']),
    ...mapGetters('auth', ['getUser']),

    sosCallService() {
      return this.sosCallServiceGetter ? this.sosCallServiceGetter() : null
    },

    overlayOpen() {
      return !!this.isOverlayOpen
    },

    activeCall() {
      return this.getSOSActiveCall
    },

    reportantName() {
      const call = this.activeCall
      if (!call) return ''
      const ud = call.userData || {}
      const name = [ud.name, ud.lastName1, ud.lastName2].filter(Boolean).join(' ').trim()
      return name || call.name || call.callbackNumber || call.phone || 'Reportante'
    },

    avatarInitial() {
      const name = this.reportantName
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    elapsedTime() {
      if (this.isClientEnded) return 'Finalizada'
      // eslint-disable-next-line no-unused-expressions
      this.nowTick
      const call = this.activeCall
      if (!call || !call.createdAt) return '00:00'
      const diffMs = Date.now() - new Date(call.createdAt).getTime()
      if (Number.isNaN(diffMs) || diffMs < 0) return '00:00'
      const totalSec = Math.floor(diffMs / 1000)
      const m = Math.floor(totalSec / 60)
      const s = totalSec % 60
      const mm = String(m).padStart(2, '0')
      const ss = String(s).padStart(2, '0')
      return `${mm}:${ss}`
    },

    videoStream() { return this.getVideoStream },
    audioStream() { return this.getAudioStream },
    isMicOn() { return this.getIsMicOn },

    isClientEnded() {
      return this.activeCall && this.activeCall.status === 'CLIENT_END_CALL'
    },

    callType() {
      return (this.activeCall && this.activeCall.type) || 'VOICE_CALL'
    },

    isVideoCall() {
      return this.callType === 'VIDEO_CALL'
    },

    isChatOnly() {
      return this.callType === 'CHAT'
    },

    callTypeLabel() {
      if (this.callType === 'CHAT') return 'Chat'
      if (this.callType === 'VOICE_CALL' || this.callType === 'CALL') return 'Llamada de voz'
      return 'SOS'
    },

    callTypeIcon() {
      if (this.callType === 'CHAT') return 'mdi-chat'
      return 'mdi-waveform'
    },

    micTitle() { return this.isMicOn ? 'Silenciar micrófono' : 'Activar micrófono' },

    address() {
      const call = this.activeCall
      if (!call) return ''
      return this.getAddressForCall(call._id) || ''
    },

    filteredMessages() {
      const all = this.getSosMessages || []
      const call = this.activeCall
      if (!call || !call._id) return []
      return all.filter((m) => m && m.emerCallId === call._id)
    },

    criticalFields() {
      const call = this.activeCall
      if (!call) return []
      const ud = call.userData || {}
      const addr = this.address
      return [
        { label: 'Teléfono', value: ud.phoneNumber || call.phone || '' },
        { label: 'Dirección', value: addr || ud.address || '' },
        { label: 'Tipo de sangre', value: ud.bloodType || '' },
        { label: 'Alergias', value: ud.alergies || '' },
      ]
    },

    extraFields() {
      const call = this.activeCall
      if (!call) return []
      const ud = call.userData || {}
      return [
        { label: 'Colonia', value: ud.neighborhood || '' },
        { label: 'Municipio', value: ud.municipality || '' },
        { label: 'Género', value: this.formatGender(ud.genre) },
        { label: 'Enfermedades', value: ud.diseases || '' },
        { label: 'Contacto emergencia', value: ud.emergencyContactName || '' },
        { label: 'Tel. emergencia', value: ud.emergencyContactPhoneNumber || '' },
        { label: 'Email', value: ud.email || '' },
      ]
    },

    hasExtraFields() {
      return this.extraFields.some((f) => !!f.value)
    },

    // Murphy/Personas solo requiere nombre O apellido paterno para disparar.
    // Si el reportante no trae ninguno de los dos, ocultamos el botón.
    canSearchPersonMurphy() {
      const call = this.activeCall
      if (!call) return false
      const ud = call.userData || {}
      return !!(ud.name || ud.lastName1)
    },

    isVideoExpanded() { return this.getIsVideoExpanded },

    canReportCad() {
      const user = this.getUser || {}
      const attrs = user.attributes || {}
      const hasPermission = !!attrs.cad_permission_add_note
      const hasCorporaciones = Array.isArray(attrs.corporaciones) && attrs.corporaciones.length > 0
      return hasPermission && hasCorporaciones
    },

    isFloating() {
      return this.sosMode === 'floating'
    },

    rootStyle() {
      if (!this.isFloating) return {}
      return {
        position: 'fixed',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        border: '1px solid var(--kln-alert-danger, rgba(208, 72, 72, 0.35))',
      }
    },
  },

  watch: {
    overlayOpen(val) {
      if (val && this.isFloating) {
        this.$nextTick(() => {
          this._clampFloatingLayoutToViewport()
          this._applyFloatingLayoutToDOM()
        })
      }
    },
    videoStream(stream) {
      this.$nextTick(() => {
        const el = this.$refs.videoEl
        if (el) el.srcObject = stream || null
        const elFloat = this.$refs.videoElFloating
        if (elFloat) elFloat.srcObject = stream || null
        const full = this.$refs.videoFullscreenEl
        if (full && this.isVideoExpanded) full.srcObject = stream || null
      })
    },
    isVideoExpanded(val) {
      this.$nextTick(() => {
        const el = this.$refs.videoFullscreenEl
        if (el) el.srcObject = val ? this.videoStream : null
      })
    },
    audioStream(stream) {
      this.$nextTick(() => {
        const refs = [this.$refs.audioEl, this.$refs.audioElFloating]
        refs.forEach((el) => {
          if (!el) return
          el.srcObject = stream || null
          if (stream && typeof el.play === 'function') {
            el.play().catch(() => { /* autoplay policy — silent */ })
          }
        })
      })
    },
    'filteredMessages.length': function () {
      this.$nextTick(() => {
        const refs = [this.$refs.chatList, this.$refs.chatListFloating]
        refs.forEach((el) => {
          if (el) el.scrollTop = el.scrollHeight
        })
      })
    },
  },

  created() {
    this._onDragMove = this._onDragMoveImpl.bind(this)
    this._onDragEnd = this._onDragEndImpl.bind(this)
    this._onResizeMove = this._onResizeMoveImpl.bind(this)
    this._onResizeEnd = this._onResizeEndImpl.bind(this)
    this._onWindowResize = this._onWindowResizeImpl.bind(this)
    this._dragOffset = null
    this._resizeState = null
  },

  mounted() {
    this.tickInterval = setInterval(() => { this.nowTick = Date.now() }, 1000)
    this.$nextTick(() => {
      // Reasignar srcObject al montar: cada cambio de modo destruye una
      // instancia de SOSPanel y monta otra (son dos <SOSPanel> separados
      // en MapV2.vue, uno por modo). La nueva instancia nace con sus refs
      // de video/audio en srcObject=null aunque el stream del store siga
      // activo. Sin esto el video se pierde al togglear docked↔floating.
      const vStream = this.videoStream
      if (vStream) {
        const vEl = this.isFloating ? this.$refs.videoElFloating : this.$refs.videoEl
        if (vEl) {
          vEl.srcObject = vStream
          if (typeof vEl.play === 'function') {
            vEl.play().catch(() => { /* autoplay policy — silent */ })
          }
        }
      }
      const aStream = this.audioStream
      if (aStream) {
        const aEl = this.isFloating ? this.$refs.audioElFloating : this.$refs.audioEl
        if (aEl) {
          aEl.srcObject = aStream
          if (typeof aEl.play === 'function') {
            aEl.play().catch(() => { /* autoplay policy — silent */ })
          }
        }
      }

      if (this.isFloating && this.overlayOpen) {
        this._clampFloatingLayoutToViewport()
        this._applyFloatingLayoutToDOM()
      }
    })
    window.addEventListener('resize', this._onWindowResize)
  },

  beforeDestroy() {
    if (this._dragOffset) {
      document.removeEventListener('mousemove', this._onDragMove)
      document.removeEventListener('mouseup', this._onDragEnd)
      this._dragOffset = null
    }
    if (this._resizeState) {
      document.removeEventListener('mousemove', this._onResizeMove)
      document.removeEventListener('mouseup', this._onResizeEnd)
      this._resizeState = null
    }
    document.body.style.cursor = ''
    window.removeEventListener('resize', this._onWindowResize)
    if (this.tickInterval) clearInterval(this.tickInterval)
  },

  methods: {
    cerrar() {
      if (!this.sosCallService) return
      this.sosCallService.cerrarOverlay()
    },

    toggleMic() {
      if (!this.sosCallService) return
      this.sosCallService.toggleMic()
    },

    swapCamera() {
      if (!this.sosCallService) return
      this.sosCallService.swapRemoteCamera()
    },

    toggleFullscreen() {
      const current = this.$store.state['sos-call-v2'].isVideoExpanded
      this.$store.commit('sos-call-v2/SET_VIDEO_EXPANDED', !current)
    },

    isOperatorMessage(msg) {
      if (!msg) return false
      const sender = (msg.sender || '').toString().toLowerCase()
      return sender === 'operator' || sender === 'oper' || msg.me === true
    },

    formatMsgTime(ts) {
      if (!ts) return ''
      try {
        const d = new Date(ts)
        if (Number.isNaN(d.getTime())) return ''
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        return `${hh}:${mm}`
      } catch (e) {
        return ''
      }
    },

    enviar() {
      const text = (this.chatInput || '').trim()
      if (!text) return
      if (!this.sosCallService) return
      this.sosCallService.sendMessage(text)
      this.chatInput = ''
    },

    // Emite al padre (MapV2.vue) la intención de abrir MurphyPanel con los
    // nombres del reportante prellenados y la búsqueda disparada. Los
    // nombres vienen descompuestos desde el backend en userData.
    buscarPersonaMurphy() {
      const call = this.activeCall
      if (!call) return
      const ud = call.userData || {}
      this.$emit('search-murphy-persona', {
        nombre: ud.name || '',
        ap1: ud.lastName1 || '',
        ap2: ud.lastName2 || '',
      })
    },

    formatGender(g) {
      if (!g) return ''
      const s = String(g).toUpperCase()
      if (s === 'M') return 'Masculino'
      if (s === 'F') return 'Femenino'
      return g
    },

    salir() {
      this.confirmSalirOpen = true
    },

    confirmarSalir() {
      this.confirmSalirOpen = false
      if (this.sosCallService) {
        // Sale de la llamada (leave_call) sin terminarla: sigue activa para los
        // demás operadores / el reportante. Paridad con "DEJAR" del legacy.
        this.sosCallService.dejarLlamada()
      }
    },

    reportarCad() {
      // Placeholder: la integración real con el flujo CAD del legacy
      // queda como follow-up (IncidentDispatch.vue o servicio CAD).
      this.$emit('report-cad', this.activeCall)
      if (window.VMA && window.VMA.showMessage) {
        window.VMA.showMessage({
          title: 'Función CAD pendiente de integración',
          color: 'info',
        })
      }
    },

    closeFullscreen() {
      this.$store.commit('sos-call-v2/SET_VIDEO_EXPANDED', false)
    },

    toggleFloatingMode() {
      if (this._dragOffset) {
        document.removeEventListener('mousemove', this._onDragMove)
        document.removeEventListener('mouseup', this._onDragEnd)
        this._dragOffset = null
      }
      if (this._resizeState) {
        document.removeEventListener('mousemove', this._onResizeMove)
        document.removeEventListener('mouseup', this._onResizeEnd)
        this._resizeState = null
      }
      this.isInteracting = false
      document.body.style.cursor = ''

      if (this.isFloating) {
        // floating → docked: abrir el slot del leftstack
        this.$store.commit('sos-call-v2/SET_SOS_MODE', 'docked')
        this.$store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: true })
        this.$nextTick(() => {
          const root = this.$refs.root
          if (root) {
            root.style.width = ''
            root.style.height = ''
            root.style.left = ''
            root.style.top = ''
          }
        })
      } else {
        // docked → floating: liberar el slot del leftstack
        const geo = this._computeFloatingGeometry()
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_POS', { x: geo.x, y: geo.y })
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_SIZE', { w: geo.width, h: geo.height })
        this.$store.commit('sos-call-v2/SET_SOS_MODE', 'floating')
        this.$store.commit('map-v2/SET_PANEL', { panel: 'sosLeft', value: false })
        this.$nextTick(() => {
          this._applyFloatingLayoutToDOM()
        })
      }
    },

    _computeFloatingGeometry() {
      const maxW = Math.floor(window.innerWidth * SOS_MAX_VW)
      const maxH = Math.floor(window.innerHeight * SOS_MAX_VH)
      const defaultW = this.isChatOnly ? FLOATING_CHAT_ONLY_W : FLOATING_DEFAULT_W
      const defaultH = this.isChatOnly ? FLOATING_CHAT_ONLY_H : FLOATING_DEFAULT_H
      const width = Math.min(maxW, Math.max(SOS_MIN_W, defaultW))
      const height = Math.min(maxH, Math.max(SOS_MIN_H, defaultH))
      const x = Math.max(12, Math.floor((window.innerWidth - width) / 2))
      const y = Math.max(12, Math.floor((window.innerHeight - height) / 4))
      return {
        x,
        y,
        width,
        height,
      }
    },

    _clampFloatingLayoutToViewport() {
      const size = this.sosFloatingSize || { w: null, h: null }
      const pos = this.sosFloatingPos || { x: 200, y: 100 }
      const maxW = Math.floor(window.innerWidth * SOS_MAX_VW)
      const maxH = Math.floor(window.innerHeight * SOS_MAX_VH)
      let w = size.w
      let h = size.h
      if (w && w > maxW) w = maxW
      if (h && h > maxH) h = maxH
      if (w && w < SOS_MIN_W) w = SOS_MIN_W
      if (h && h < SOS_MIN_H) h = SOS_MIN_H
      const maxX = Math.max(0, window.innerWidth - MIN_VISIBLE_X)
      const maxY = Math.max(0, window.innerHeight - MIN_VISIBLE_Y)
      const x = Math.max(0, Math.min(maxX, pos.x))
      const y = Math.max(0, Math.min(maxY, pos.y))
      if (w !== size.w || h !== size.h) {
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_SIZE', { w, h })
      }
      if (x !== pos.x || y !== pos.y) {
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_POS', { x, y })
      }
    },

    _applyFloatingLayoutToDOM() {
      const root = this.$refs.root
      if (!root) return
      const pos = this.sosFloatingPos || { x: 200, y: 100 }
      root.style.left = pos.x + 'px'
      root.style.top = pos.y + 'px'
      const size = this.sosFloatingSize || { w: null, h: null }
      if (size.w && size.h) {
        root.style.width = size.w + 'px'
        root.style.height = size.h + 'px'
      } else {
        const geo = this._computeFloatingGeometry()
        root.style.width = geo.width + 'px'
        root.style.height = geo.height + 'px'
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_SIZE', { w: geo.width, h: geo.height })
      }
    },

    _onWindowResizeImpl() {
      if (!this.isFloating) return
      if (this._dragOffset || this._resizeState) return
      this._clampFloatingLayoutToViewport()
      this._applyFloatingLayoutToDOM()
    },

    onHeaderMouseDown(e) {
      if (!this.isFloating) return
      const excluded = 'button, input, select, textarea, a, .v-btn, .v-input, .sos-resize-handle'
      if (e.target.closest(excluded)) return
      e.preventDefault()
      const pos = this.sosFloatingPos || { x: 200, y: 100 }
      this._dragOffset = {
        x: e.clientX - pos.x,
        y: e.clientY - pos.y,
        currentX: pos.x,
        currentY: pos.y,
      }
      this.isInteracting = true
      document.body.style.cursor = 'grabbing'
      document.addEventListener('mousemove', this._onDragMove)
      document.addEventListener('mouseup', this._onDragEnd)
    },

    _onDragMoveImpl(e) {
      if (!this._dragOffset) return
      const maxX = Math.max(0, window.innerWidth - MIN_VISIBLE_X)
      const maxY = Math.max(0, window.innerHeight - MIN_VISIBLE_Y)
      const x = Math.max(0, Math.min(maxX, e.clientX - this._dragOffset.x))
      const y = Math.max(0, Math.min(maxY, e.clientY - this._dragOffset.y))
      const root = this.$refs.root
      if (root) {
        root.style.left = x + 'px'
        root.style.top = y + 'px'
      }
      this._dragOffset.currentX = x
      this._dragOffset.currentY = y
    },

    _onDragEndImpl() {
      if (this._dragOffset) {
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_POS', {
          x: this._dragOffset.currentX,
          y: this._dragOffset.currentY,
        })
      }
      this._dragOffset = null
      this.isInteracting = false
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', this._onDragMove)
      document.removeEventListener('mouseup', this._onDragEnd)
    },

    _startResize(direction, e) {
      if (!this.isFloating) return
      e.preventDefault()
      e.stopPropagation()
      const root = this.$refs.root
      if (!root) return
      const pos = this.sosFloatingPos || { x: 200, y: 100 }
      const rect = root.getBoundingClientRect()
      this._resizeState = {
        dir: direction,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startX: pos.x,
        startY: pos.y,
        startW: rect.width,
        startH: rect.height,
        currentX: pos.x,
        currentY: pos.y,
        currentW: rect.width,
        currentH: rect.height,
      }
      this.isInteracting = true
      document.body.style.cursor = direction + '-resize'
      document.addEventListener('mousemove', this._onResizeMove)
      document.addEventListener('mouseup', this._onResizeEnd)
    },

    _onResizeMoveImpl(e) {
      if (!this._resizeState) return
      const s = this._resizeState
      let dx = e.clientX - s.startMouseX
      let dy = e.clientY - s.startMouseY
      const maxW = Math.floor(window.innerWidth * SOS_MAX_VW)
      const maxH = Math.floor(window.innerHeight * SOS_MAX_VH)
      let newX = s.startX
      let newY = s.startY
      let newW = s.startW
      let newH = s.startH

      if (s.dir.indexOf('e') !== -1) {
        newW = Math.max(SOS_MIN_W, Math.min(maxW, s.startW + dx))
      } else if (s.dir.indexOf('w') !== -1) {
        let tentativeW = s.startW - dx
        if (tentativeW < SOS_MIN_W) {
          tentativeW = SOS_MIN_W
          dx = s.startW - SOS_MIN_W
        } else if (tentativeW > maxW) {
          tentativeW = maxW
          dx = s.startW - maxW
        }
        newW = tentativeW
        newX = s.startX + dx
      }

      if (s.dir.indexOf('s') !== -1) {
        newH = Math.max(SOS_MIN_H, Math.min(maxH, s.startH + dy))
      } else if (s.dir.indexOf('n') !== -1) {
        let tentativeH = s.startH - dy
        if (tentativeH < SOS_MIN_H) {
          tentativeH = SOS_MIN_H
          dy = s.startH - SOS_MIN_H
        } else if (tentativeH > maxH) {
          tentativeH = maxH
          dy = s.startH - maxH
        }
        newH = tentativeH
        newY = s.startY + dy
      }

      newX = Math.max(0, Math.min(window.innerWidth - MIN_VISIBLE_X, newX))
      newY = Math.max(0, Math.min(window.innerHeight - MIN_VISIBLE_Y, newY))

      const root = this.$refs.root
      if (!root) return
      root.style.width = newW + 'px'
      root.style.height = newH + 'px'
      root.style.left = newX + 'px'
      root.style.top = newY + 'px'

      s.currentX = newX
      s.currentY = newY
      s.currentW = newW
      s.currentH = newH
    },

    _onResizeEndImpl() {
      if (this._resizeState) {
        const s = this._resizeState
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_POS', { x: s.currentX, y: s.currentY })
        this.$store.commit('sos-call-v2/SET_SOS_FLOATING_SIZE', { w: s.currentW, h: s.currentH })
      }
      this._resizeState = null
      this.isInteracting = false
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', this._onResizeMove)
      document.removeEventListener('mouseup', this._onResizeEnd)
    },
  },
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   SHARED — header, ficha, chat (compartido docked + floating)
   ═══════════════════════════════════════════════════════════ */

.sos-panel-wrapper {
  display: contents;
}

.sos-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--kln-bg-surface, #0d1220);
  color: var(--kln-text);
  overflow: hidden;
}

.sos-panel--docked {
  width: 100%;
}

.sos-panel--floating {
  position: fixed;
  border-radius: 12px;
  border: 1px solid var(--kln-alert-danger, rgba(208, 72, 72, 0.6));
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 2200;
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────── */
.sos-panel__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--kln-bg-deep, rgba(0, 0, 0, 0.3));
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.sos-panel__header--draggable {
  cursor: move;
  user-select: none;
}

.sos-panel__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--kln-alert-danger, #d04848);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
}

.sos-panel__header-body {
  flex: 1;
  min-width: 0;
}

.sos-panel__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sos-panel__timer {
  font-size: 11px;
  color: var(--kln-text-dim);
  display: flex;
  align-items: center;
}

.sos-panel__header-btn {
  flex-shrink: 0;
}

.sos-panel__header-finalize {
  flex-shrink: 0;
  margin-left: 4px;
}

/* ── Media (video / audio-only) — modo docked ───────── */
.sos-panel__media {
  flex-shrink: 0;
  height: var(--kln-sos-video-height, 280px);
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.sos-panel__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sos-panel__audio-only {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--kln-text);
}

.sos-panel__audio-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--kln-alert-danger, #d04848);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 22px;
}

.sos-panel__audio-label {
  font-size: 11px;
  color: var(--kln-text-dim);
}

/* ── Video controls overlay ─────────────────────────── */
.sos-panel__video-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 12px;
  background: linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0));
  display: flex;
  justify-content: center;
  gap: 4px;
}

.sos-panel__video-controls >>> .v-btn {
  background: rgba(0, 0, 0, 0.4);
}

/* ── Ficha crítica ──────────────────────────────────── */
.sos-panel__critical {
  flex-shrink: 0;
  padding: 10px 12px;
  background: var(--kln-bg-surface, #0d1220);
  border-bottom: 1px solid var(--kln-border-dim);
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
}

.sos-panel__field {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sos-panel__field-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-text-dim);
}

.sos-panel__field-value {
  font-size: 12px;
  color: var(--kln-text);
  word-break: break-word;
}

.sos-panel__critical-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.sos-panel__expand-btn {
  background: transparent;
  border: none;
  color: var(--kln-text-dim);
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  padding: 2px 0;
}

.sos-panel__expand-btn:hover {
  color: var(--kln-text);
}

.sos-panel__murphy-btn {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: 1px solid var(--kln-border-dim);
  color: var(--kln-text);
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.sos-panel__murphy-btn:hover {
  background: var(--kln-border-dim);
  border-color: var(--kln-accent);
  color: var(--kln-accent);
}

/* ── Chat ───────────────────────────────────────────── */
.sos-panel__chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sos-panel__chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sos-panel__chat-row {
  display: flex;
}

.sos-panel__chat-row.is-operator {
  justify-content: flex-end;
}

.sos-panel__chat-bubble {
  max-width: 80%;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--kln-bg-deep, rgba(0, 0, 0, 0.25));
  color: var(--kln-text);
  font-size: 12px;
  position: relative;
}

.sos-panel__chat-row.is-operator .sos-panel__chat-bubble {
  background: var(--kln-alert-danger, #d04848);
  color: #fff;
}

.sos-panel__chat-sender {
  display: block;
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 2px;
  color: var(--kln-text-dim);
}

.sos-panel__chat-row.is-operator .sos-panel__chat-sender {
  color: rgba(255, 255, 255, 0.85);
}

.sos-panel__chat-time {
  display: block;
  font-size: 9px;
  color: var(--kln-text-dim);
  margin-top: 2px;
  text-align: right;
}

.sos-panel__chat-row.is-operator .sos-panel__chat-time {
  color: rgba(255, 255, 255, 0.7);
}

.sos-panel__chat-empty {
  text-align: center;
  color: var(--kln-text-dim);
  font-size: 11px;
  padding: 20px 0;
}

.sos-panel__chat-input-row {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-top: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
  gap: 4px;
}

.sos-panel__ended-alert {
  margin: 8px 10px;
  font-size: 11px;
}

/* ═══════════════════════════════════════════════════════════
   FLOATING — body 2 cols (video izq | info+chat der)
   ═══════════════════════════════════════════════════════════ */

.sos-panel__floating-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.sos-panel__floating-media {
  flex: 0 0 40%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--kln-border-dim);
  position: relative;
}

.sos-panel__video--floating {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sos-panel__audio-only--floating {
  gap: 10px;
}

.sos-panel__floating-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.sos-panel__critical--floating {
  padding: 8px 12px;
}

.sos-panel__chat--floating {
  flex: 1;
  min-height: 0;
}

/* ═══════════════════════════════════════════════════════════
   FLOATING — interaction overlay + 8 resize handles
   (idéntico al SOSOverlay actual)
   ═══════════════════════════════════════════════════════════ */

.sos-interaction-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  cursor: inherit;
}

.sos-resize-handle {
  position: absolute;
  z-index: 10;
  background: transparent;
}
.sos-resize-handle--n { top: -4px; left: 8px; right: 8px; height: 8px; cursor: ns-resize; }
.sos-resize-handle--s { bottom: -4px; left: 8px; right: 8px; height: 8px; cursor: ns-resize; }
.sos-resize-handle--e { right: -4px; top: 8px; bottom: 8px; width: 8px; cursor: ew-resize; }
.sos-resize-handle--w { left: -4px; top: 8px; bottom: 8px; width: 8px; cursor: ew-resize; }
.sos-resize-handle--nw { top: -4px; left: -4px; width: 12px; height: 12px; cursor: nwse-resize; }
.sos-resize-handle--ne { top: -4px; right: -4px; width: 12px; height: 12px; cursor: nesw-resize; }
.sos-resize-handle--sw { bottom: -4px; left: -4px; width: 12px; height: 12px; cursor: nesw-resize; }
.sos-resize-handle--se { bottom: -4px; right: -4px; width: 12px; height: 12px; cursor: nwse-resize; }

/* ── Fullscreen video overlay ───────────────────────── */
.sos-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.sos-fullscreen-box {
  position: relative;
  width: 80vw;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
}

.sos-fullscreen-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sos-fullscreen-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5) !important;
}

.sos-fullscreen-enter-active,
.sos-fullscreen-leave-active {
  transition: opacity 0.2s ease-out;
}
.sos-fullscreen-enter,
.sos-fullscreen-leave-to {
  opacity: 0;
}

.sos-confirm-card {
  background: var(--kln-bg-surface) !important;
  color: var(--kln-text) !important;
}
</style>
